import {
  DIFFICULTY_LABELS,
  sourceTypeLabel,
  type QuestionBankMeta,
  type QuestionFilters,
} from "@/hooks/useQuestionBank";
import {useSiteSettingsContext} from "@/hooks/useSiteSettingsContext";

interface FilterBarProps {
  meta: QuestionBankMeta | null;
  filters: QuestionFilters;
  onChange: (patch: Partial<QuestionFilters>) => void;
}

function distinct(values: string[]): string[] {
  return Array.from(new Set(values.filter((v) => v !== ""))).sort();
}

function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold text-foreground-500">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 px-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 cursor-pointer whitespace-nowrap"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export default function FilterBar({ meta, filters, onChange }: FilterBarProps) {
  const { settings } = useSiteSettingsContext();
  const name = settings.siteName || "DersDinle";
  const facets = meta?.facets ?? [];
  const lessons = meta?.lessons ?? [];

  const { lesson, topic, difficulty, sourceType } = filters;

  // Kademeli (basamaklı) filtreleme: her seçim, sonraki menülerin
  // seçeneklerini gerçekte var olan sorulara göre daraltır.
  const topicFacets = lesson ? facets.filter((f) => f.lesson === lesson) : facets;
  const difficultyFacets = topic ? topicFacets.filter((f) => f.topic === topic) : topicFacets;
  const sourceTypeFacets = difficulty
    ? difficultyFacets.filter((f) => f.difficulty === difficulty)
    : difficultyFacets;
  const yearFacets = sourceType ? sourceTypeFacets.filter((f) => f.sourceType === sourceType) : sourceTypeFacets;

  const topicOptions = distinct(topicFacets.map((f) => f.topic));
  const difficultyOptions = distinct(difficultyFacets.map((f) => f.difficulty));
  const sourceTypeOptions = distinct(sourceTypeFacets.map((f) => f.sourceType));
  const yearOptions = distinct(yearFacets.map((f) => f.year));

  function changeLesson(v: string) {
    onChange({ lesson: v, topic: "", difficulty: "", sourceType: "", sourceName: "", year: "" });
  }
  function changeTopic(v: string) {
    onChange({ topic: v, difficulty: "", sourceType: "", sourceName: "", year: "" });
  }
  function changeDifficulty(v: string) {
    onChange({ difficulty: v, sourceType: "", sourceName: "", year: "" });
  }
  function changeSourceType(v: string) {
    onChange({ sourceType: v, sourceName: "", year: "" });
  }
  function changeYear(v: string) {
    onChange({ year: v });
  }

  const limitOptions = ["10", "20", "50"].map((n) => ({ value: n, label: `${n} Soru` }));

  return (
    <div className="flex flex-wrap gap-4">
      <Select
        label="Ders"
        value={lesson}
        onChange={changeLesson}
        options={[{ value: "", label: "Tümü" }, ...lessons.map((l) => ({ value: l, label: l }))]}
      />
      <Select
        label="Konu"
        value={topic}
        onChange={changeTopic}
        options={[{ value: "", label: "Tümü" }, ...topicOptions.map((t) => ({ value: t, label: t }))]}
      />
      <Select
        label="Zorluk"
        value={difficulty}
        onChange={changeDifficulty}
        options={[
          { value: "", label: "Tümü" },
          ...difficultyOptions.map((d) => ({ value: d, label: DIFFICULTY_LABELS[d] ?? d })),
        ]}
      />
      <Select
        label="Soru Türü"
        value={sourceType}
        onChange={changeSourceType}
        options={[
          { value: "", label: "Tümü" },
          ...sourceTypeOptions.map((s) => ({ value: s, label: sourceTypeLabel(s, name) })),
        ]}
      />
      <Select
        label="Yıl"
        value={filters.year}
        onChange={changeYear}
        options={[{ value: "", label: "Tümü" }, ...yearOptions.map((y) => ({ value: y, label: y }))]}
      />
      <Select
        label="Soru Sayısı"
        value={String(filters.limit)}
        onChange={(v) => onChange({ limit: Number(v) || 10 })}
        options={limitOptions}
      />
    </div>
  );
}